import { useLayoutEffect, useRef } from 'react';

/** Never shrink below this share of the natural size - past it the text is unreadable anyway. */
const MIN_SCALE = 0.55;

/**
 * Shrinks the text inside a flex row until it fits its box. Attach the
 * returned ref to the element whose children size themselves in `em`; the
 * hook sets that element's font-size (in em) straight on the node, so nothing
 * renders for the fit. Measures in a layout effect, before paint, so the
 * reset-and-shrink is never seen. Re-measures whenever `value` changes and on resize.
 */
export const useFitText = <T extends HTMLElement>(value: unknown) => {
  const nodeRef = useRef<T>(null);

  useLayoutEffect(() => {
    const node = nodeRef.current;
    if (!node) {
      return undefined;
    }

    const fit = () => {
      // Measure at natural size first, otherwise a shrunken text never grows back.
      node.style.fontSize = '';
      const available = node.clientWidth;
      const needed = node.scrollWidth;
      if (!available || needed <= available) {
        return;
      }
      node.style.fontSize = `${Math.max(available / needed, MIN_SCALE).toFixed(3)}em`;
    };

    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [value]);

  return nodeRef;
};

export default useFitText;
